// Epoch 19: Lead Generator Enhancements - Apollo-Style Lead Generator V2
// File: src/pages/LeadGeneratorV2.tsx
// Purpose: Complete Apollo-style lead generation interface with multi-select filtering

import React, { useState } from 'react';
import { supabase } from '../supabase/client';
// Header is provided by AuthenticatedRoute wrapper
import FilterSidebar from '../components/filters/FilterSidebar';
import MobileFilterOverlay from '../components/filters/MobileFilterOverlay';
import EnhancedLeadPreview from '../components/filters/EnhancedLeadPreview';
import CostPreviewModal from '../components/billing/CostPreviewModal';
import QuotaDisplay from '../components/billing/QuotaDisplay';
import { useMultiSelectFilters } from '../hooks/useMultiSelectFilters';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { MultiSelectFilterState } from '../types/filters';
import { PromoCodeValidationResponse, TierCounts } from '../types/billing';

const LeadGeneratorV2: React.FC = () => {
  // Responsive design hooks
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  // Filter state management
  const {
    filters,
    toggleFilterOption,
    clearFilter,
    clearAllFilters,
    hasActiveFilters,
    metadata,
    loading: filtersLoading,
    error: filtersError
  } = useMultiSelectFilters();

  // UI state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Export and cost preview state
  const [showCostPreview, setShowCostPreview] = useState(false);
  const [costPreviewData, setCostPreviewData] = useState<{
    leadCount: number;
    tierBreakdown: TierCounts;
    filters: MultiSelectFilterState;
    estimatedCost: number;
  } | null>(null);


  // Handle export request from preview component
  const handleExportRequest = async (leadCount: number, estimatedCost: number) => {
    try {
      // Validate that at least one filter is selected (excluding default tiers)
      const hasActualFilters = 
        (filters.countries && filters.countries.length > 0) ||
        (filters.cities && filters.cities.length > 0) ||
        (filters.jobLevels && filters.jobLevels.length > 0) ||
        (filters.jobFunctions && filters.jobFunctions.length > 0) ||
        (filters.industries && filters.industries.length > 0);
      
      if (!hasActualFilters) {
        throw new Error('Please select at least one filter before exporting leads');
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required');
      }

      console.log('Exporting with filters:', filters);
      
      // First try to export to check if payment is required
      const response = await fetch(
        'https://zeeovxbtdyxncikfonij.supabase.co/functions/v1/exportLeads',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ filters, createCheckout: false })
        }
      );

      if (response.status === 402) {
        // Payment required - show cost preview modal
        const paymentData = await response.json();
        
        setCostPreviewData({
          leadCount: paymentData.leadsCount || leadCount,
          tierBreakdown: paymentData.pricing?.tierCounts || {},
          filters,
          estimatedCost: paymentData.pricing?.totalCost || estimatedCost
        });
        setShowCostPreview(true);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Export failed');
      }

      // Successful export - download CSV
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Show success message
      alert(`Successfully exported ${leadCount} leads!`);
    } catch (err) {
      console.error('Export error:', err);
      alert(err instanceof Error ? err.message : 'Export failed');
    }
  };

  // Handle cost preview confirmation (create Stripe checkout)
  const handleCostPreviewConfirm = async (finalCost: number, promoCode?: PromoCodeValidationResponse) => {
    setShowCostPreview(false);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required');
      }

      const checkoutData: {
        filters: MultiSelectFilterState;
        createCheckout: boolean;
        promoCode?: string;
        stripePromotionCodeId?: string;
      } = { 
        filters,
        createCheckout: true
      };

      if (promoCode?.isValid) {
        checkoutData.promoCode = promoCode.code;
        checkoutData.stripePromotionCodeId = promoCode.stripePromotionCodeId;
      }
      
      // Validate filters before sending for checkout (excluding default tiers)
      const hasActualFilters = 
        (filters.countries && filters.countries.length > 0) ||
        (filters.cities && filters.cities.length > 0) ||
        (filters.jobLevels && filters.jobLevels.length > 0) ||
        (filters.jobFunctions && filters.jobFunctions.length > 0) ||
        (filters.industries && filters.industries.length > 0);
      
      if (!hasActualFilters) {
        throw new Error('Please select at least one filter before exporting leads');
      }
      
      const response = await fetch(
        'https://zeeovxbtdyxncikfonij.supabase.co/functions/v1/exportLeads',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(checkoutData)
        }
      );
      
      if (response.status === 402) {
        const data = await response.json();
        if (data.stripeCheckoutUrl) {
          window.location.href = data.stripeCheckoutUrl;
        } else {
          console.error('Response data:', data);
          throw new Error('Checkout URL not found in response');
        }
      } else {
        const errorData = await response.text();
        console.error('Export endpoint error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorData
        });
        throw new Error(`Export endpoint returned status ${response.status}: ${errorData}`);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert(err instanceof Error ? err.message : 'Failed to create checkout');
    }
  };

  const handleCostPreviewCancel = () => {
    setShowCostPreview(false);
    setCostPreviewData(null);
  };

  // Toggle mobile filter overlay
  const handleMobileFilterToggle = () => {
    setMobileFilterOpen(!mobileFilterOpen);
  };

  // Toggle sidebar collapse on tablet/desktop
  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Responsive layout configuration
  const getLayoutClasses = () => {
    if (isMobile) {
      return {
        container: 'min-h-screen bg-gray-50',
        content: 'flex flex-col',
        main: 'flex-1 p-4'
      };
    }

    if (isTablet) {
      return {
        container: 'min-h-screen bg-gray-50',
        content: 'flex',
        main: sidebarCollapsed ? 'flex-1 ml-12' : 'flex-1 ml-80'
      };
    }

    return {
      container: 'min-h-screen bg-gray-50',
      content: 'flex',
      main: sidebarCollapsed ? 'flex-1 ml-12' : 'flex-1 ml-80'
    };
  };

  const layoutClasses = getLayoutClasses();

  // Loading state
  if (filtersLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Lead Generator...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (filtersError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-semibold">Error Loading Lead Generator</p>
            <p className="text-sm mt-2">{filtersError}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutClasses.container}>
      
      <div className={layoutClasses.content}>
        {/* Desktop/Tablet Sidebar */}
        {!isMobile && (
          <div className="fixed top-16 left-0 bottom-0 z-20">
            <FilterSidebar
              filters={filters}
              metadata={metadata}
              onToggleOption={toggleFilterOption}
              onClearFilter={clearFilter}
              onClearAll={clearAllFilters}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={handleSidebarToggle}
            />
          </div>
        )}

        {/* Mobile Filter Overlay */}
        {isMobile && (
          <MobileFilterOverlay
            isOpen={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            filters={filters}
            metadata={metadata}
            onToggleOption={toggleFilterOption}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        {/* Main Content Area */}
        <main className={`${layoutClasses.main} transition-all duration-300 ease-in-out pt-16`}>
          <div className="max-w-7xl mx-auto">
            {/* Mobile Header with Filter Button */}
            {isMobile && (
              <div className="flex items-center justify-between mb-6 bg-white rounded-lg shadow-sm p-4">
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Lead Generator</h1>
                  <p className="text-sm text-gray-600">Build your lead list using the filters</p>
                </div>
                <button
                  onClick={handleMobileFilterToggle}
                  className="relative bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <span>Filters</span>
                  {hasActiveFilters && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
                  )}
                </button>
              </div>
            )}

            {/* Desktop/Tablet Header */}
            {!isMobile && (
              <div className="mb-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">Lead Generator</h1>
                      <p className="text-gray-600 mt-1">
                        Build your lead list using the filters on the left hand side.
                      </p>
                    </div>
                    <div className="text-right">
                      <QuotaDisplay variant="compact" showUpgradePrompt={false} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Lead Preview Section */}
            <div className="space-y-6">
              {/* Mobile Quota Display */}
              {isMobile && (
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <QuotaDisplay variant="page" showUpgradePrompt={true} />
                </div>
              )}

              {/* Enhanced Lead Preview */}
              <EnhancedLeadPreview
                filters={filters}
                onExportRequest={handleExportRequest}
                className="shadow-sm"
              />
            </div>
          </div>
        </main>
      </div>

      {/* Cost Preview Modal */}
      {costPreviewData && (
        <CostPreviewModal
          isOpen={showCostPreview}
          leadCount={costPreviewData.leadCount}
          tierBreakdown={costPreviewData.tierBreakdown}
          filters={costPreviewData.filters}
          onConfirm={handleCostPreviewConfirm}
          onCancel={handleCostPreviewCancel}
        />
      )}
    </div>
  );
};

export default LeadGeneratorV2;